import type { ValidationIssue, ValidationResult } from '../exercises/types.ts';
import { TEMPO_MAX, TEMPO_MIN, TIME_SIGNATURE_BY_ID, TIME_SIGNATURES, getTimeSignature, groupingLabel, isGroupingFor } from './meter.ts';

export const NOTE_VALUES = ['whole', 'half', 'quarter', 'eighth', 'sixteenth'] as const;
export type NoteValue = (typeof NOTE_VALUES)[number];

export const NOTE_VALUE_LABEL: Record<NoteValue, string> = {
  whole: 'Whole notes',
  half: 'Half notes',
  quarter: 'Quarter notes',
  eighth: 'Eighth notes',
  sixteenth: 'Sixteenth notes',
};

/**
 * What the learner taps: every note of a rhythm ("rhythm", the default), or only
 * the strong beats — the first beat of each group (simple meters, and compound
 * meters counted in beats), or each dotted-quarter beat when a compound meter is
 * counted in eighths — while the metronome plays every pulse without accents.
 */
export type RhythmMode = 'rhythm' | 'strong-beats';

/** Compound meters only: count the dotted-quarter beats (default) or the eighth notes (e.g. ♪ = 150). */
export type CountUnit = 'beat' | 'eighth';

/** Slowest tempo, in beats, when a compound meter is counted in eighths (♪ = 90). */
export const TEMPO_MIN_EIGHTH_COUNT = 30;

export const RHYTHM_QUESTION_COUNT_MIN = 3;
export const RHYTHM_QUESTION_COUNT_MAX = 20;

/** Rhythm-tapping practice settings. */
export interface RhythmConfig {
  timeSignature: string;
  /** Beats per minute, counted in the meter's beat (dotted quarter in 6/8). */
  tempo: number;
  /** Measures per phrase. */
  measures: 1 | 2;
  /** Note values that may appear (compound meters' dotted beat counts as "quarter"). */
  noteValues: NoteValue[];
  /** Dotted rhythms (dotted quarter + eighth, dotted eighth + sixteenth…). */
  dotted: boolean;
  rests: boolean;
  /** Ties and syncopation across beats and barlines. */
  ties: boolean;
  /** Eighth- and quarter-note triplets (simple meters only). Missing in configs saved before triplets existed: off. */
  triplets: boolean;
  questionCount: number;
  /** Beat grouping, for meters that allow more than one (e.g. [2, 3] in 5/4). Missing: the meter's default. */
  grouping?: number[];
  /** Compound meters only. Missing: count beats. `tempo` is always in beats (♪ = 3 × tempo when counting eighths). */
  countUnit?: CountUnit;
  /** Missing: tap the rhythm. */
  mode?: RhythmMode;
}

export function defaultRhythmConfig(timeSignature = '4/4'): RhythmConfig {
  return {
    timeSignature,
    tempo: 80,
    measures: 2,
    noteValues: ['half', 'quarter', 'eighth'],
    dotted: false,
    rests: true,
    ties: false,
    triplets: false,
    questionCount: 6,
  };
}

function issue(path: string, code: string, message: string): ValidationIssue {
  return { path, code, message };
}

/** `minQuestions`: one part of a multi-part rhythm lesson may hold fewer questions than a whole session. */
export function validateRhythmConfig(input: unknown, options: { minQuestions?: number } = {}): ValidationResult<RhythmConfig> {
  const minQuestions = options.minQuestions ?? RHYTHM_QUESTION_COUNT_MIN;
  if (typeof input !== 'object' || input === null) return { ok: false, issues: [issue('', 'invalid_type', 'Configuration must be an object.')] };
  const raw = input as Record<string, unknown>;
  const issues: ValidationIssue[] = [];

  const timeSignature = typeof raw.timeSignature === 'string' && TIME_SIGNATURE_BY_ID.has(raw.timeSignature) ? raw.timeSignature : null;
  if (!timeSignature) issues.push(issue('timeSignature', 'invalid_time_signature', `Choose one of ${TIME_SIGNATURES.map((t) => t.id).join(', ')}.`));

  const ts0 = timeSignature ? getTimeSignature(timeSignature) : null;
  const countUnit = raw.countUnit === undefined || raw.countUnit === 'beat' ? 'beat' : raw.countUnit === 'eighth' && ts0?.kind === 'compound' ? 'eighth' : null;
  if (countUnit === null) issues.push(issue('countUnit', 'invalid_count_unit', 'Counting in eighth notes is only for compound meters (6/8, 9/8, 12/8).'));
  const tempoMin = countUnit === 'eighth' ? TEMPO_MIN_EIGHTH_COUNT : TEMPO_MIN;
  const tempo = raw.tempo;
  if (typeof tempo !== 'number' || !Number.isInteger(tempo) || tempo < tempoMin || tempo > TEMPO_MAX) {
    issues.push(issue('tempo', 'invalid_tempo', `Tempo must be a whole number from ${tempoMin} to ${TEMPO_MAX}.`));
  }
  let grouping: number[] | undefined;
  if (raw.grouping !== undefined) {
    if (!Array.isArray(raw.grouping) || !raw.grouping.every((n) => typeof n === 'number') || !ts0 || !isGroupingFor(ts0, raw.grouping as number[])) {
      issues.push(issue('grouping', 'invalid_grouping', ts0 ? `${ts0.id} groups its beats as ${ts0.groupings.map(groupingLabel).join(' or ')}.` : 'Choose a time signature first.'));
    } else {
      grouping = [...(raw.grouping as number[])];
    }
  }
  const mode = raw.mode === undefined || raw.mode === 'rhythm' ? 'rhythm' : raw.mode === 'strong-beats' ? 'strong-beats' : null;
  if (mode === null) issues.push(issue('mode', 'invalid_mode', 'Choose tapping the rhythm or the strong beats.'));
  const measures = raw.measures;
  if (measures !== 1 && measures !== 2) issues.push(issue('measures', 'invalid_measures', 'Choose one or two measures.'));

  let noteValues: NoteValue[] = [];
  if (!Array.isArray(raw.noteValues) || raw.noteValues.length === 0 || !raw.noteValues.every((v) => (NOTE_VALUES as readonly unknown[]).includes(v))) {
    issues.push(issue('noteValues', 'invalid_note_values', 'Choose at least one note value.'));
  } else {
    noteValues = NOTE_VALUES.filter((v) => (raw.noteValues as string[]).includes(v));
  }
  for (const key of ['dotted', 'rests', 'ties'] as const) {
    if (typeof raw[key] !== 'boolean') issues.push(issue(key, 'invalid_type', 'Expected true or false.'));
  }
  if (raw.triplets !== undefined && typeof raw.triplets !== 'boolean') issues.push(issue('triplets', 'invalid_type', 'Expected true or false.'));
  const questionCount = raw.questionCount;
  if (typeof questionCount !== 'number' || !Number.isInteger(questionCount) || questionCount < minQuestions || questionCount > RHYTHM_QUESTION_COUNT_MAX) {
    issues.push(issue('questionCount', 'invalid_question_count', `Questions must be a whole number from ${minQuestions} to ${RHYTHM_QUESTION_COUNT_MAX}.`));
  }
  if (issues.length > 0) return { ok: false, issues };

  const config: RhythmConfig = {
    timeSignature: timeSignature as string,
    tempo: tempo as number,
    measures: measures as 1 | 2,
    noteValues,
    dotted: raw.dotted as boolean,
    rests: raw.rests as boolean,
    ties: raw.ties as boolean,
    triplets: raw.triplets === true,
    questionCount: questionCount as number,
    // Optional fields are stored only when set, so older configs keep their exact form.
    ...(grouping ? { grouping } : {}),
    ...(countUnit === 'eighth' ? { countUnit: 'eighth' as const } : {}),
    ...(mode === 'strong-beats' ? { mode: 'strong-beats' as const } : {}),
  };
  // Tapping strong beats needs no particular note values.
  if (config.mode === 'strong-beats') return { ok: true, value: config };
  // Every beat must be fillable with the chosen note values.
  const ts = getTimeSignature(config.timeSignature);
  const fits = ts.kind === 'compound' ? config.noteValues.some((v) => v === 'quarter' || v === 'eighth' || v === 'sixteenth') : ts.denominator === 2
      ? config.noteValues.some((v) => v !== 'whole')
      : config.noteValues.some((v) => v === 'quarter' || v === 'eighth' || v === 'sixteenth');
  if (!fits) {
    issues.push(
      issue(
        'noteValues',
        'values_do_not_fit',
        ts.kind === 'compound'
          ? 'Compound meters need quarter, eighth or sixteenth notes to fill each dotted-quarter beat.'
          : 'Add quarter, eighth or sixteenth notes so every beat can be filled.',
      ),
    );
    return { ok: false, issues };
  }
  if (config.triplets) {
    if (ts.kind === 'compound') {
      return { ok: false, issues: [issue('triplets', 'triplets_in_compound', 'Triplets are for simple meters — compound meters already divide the beat in three.')] };
    }
    if (!config.noteValues.includes('eighth') && !config.noteValues.includes('quarter')) {
      return { ok: false, issues: [issue('triplets', 'triplets_need_values', 'Triplets need eighth notes (eighth-note triplets) or quarter notes (quarter-note triplets).')] };
    }
  }
  return { ok: true, value: config };
}

/** Tempo as written for the learner: "♩ = 66", "𝅗𝅥 = 60", "♩. = 60", or "♪ = 150" when a compound meter is counted in eighths. */
export function tempoMarking(timeSignature: string, tempo: number, countUnit?: CountUnit): string {
  const ts = getTimeSignature(timeSignature);
  if (ts.kind === 'compound') return countUnit === 'eighth' ? `♪ = ${tempo * 3}` : `♩. = ${tempo}`;
  return ts.denominator === 2 ? `𝅗𝅥 = ${tempo}` : `♩ = ${tempo}`;
}

/** "5/4 (2+3)" when the meter has more than one grouping; otherwise just the meter. */
export function meterLabel(timeSignature: string, grouping?: readonly number[] | null): string {
  const ts = getTimeSignature(timeSignature);
  return ts.groupings.length > 1 ? `${ts.id} (${groupingLabel(grouping && isGroupingFor(ts, grouping) ? grouping : ts.groupings[0]!)})` : ts.id;
}

export function describeRhythmConfig(config: RhythmConfig): string {
  const where = `${meterLabel(config.timeSignature, config.grouping)} at ${tempoMarking(config.timeSignature, config.tempo, config.countUnit)}`;
  if (config.mode === 'strong-beats') return `Strong beats · ${where} · ${config.questionCount} questions`;
  const extras = [config.dotted && 'dotted', config.rests && 'rests', config.ties && 'ties', config.triplets && 'triplets'].filter(Boolean).join(', ');
  return `${where} · ${config.noteValues.join(', ')}${extras ? ` · ${extras}` : ''} · ${config.questionCount} questions`;
}
